import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { trackEvent } from '../utils/analytics';
import LocationSelector from '../components/LocationSelector';
import { findStateCode } from '../data/countries';
import FAQ from '../components/FAQ';
import CommunityStats from '../components/CommunityStats';
import OtherIdentityPrompt from '../components/OtherIdentityPrompt';
import RequestCategoryModal from '../components/RequestCategoryModal';
import TrustBadge from '../components/TrustBadge';
import { setCanonical } from '../utils/seo';
import { getCollegesForCity, getGraduationYearOptions } from '../data/colleges';

function Register() {
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: profile, 4: identity, 5: complete profile (optional)
  const [formData, setFormData] = useState({
    verifyEmail: '', // email used for OTP verification
    code: '',
    firstName: '',
    lastName: '',
    email: '',
    gender: '',
    hometownCity: '',
    hometownState: '',
    hometownCountry: '',
    currentCity: '',
    currentState: '',
    currentCountry: '',
    age: '',
    profession: '',
    interests: '',
    isStudent: false,
    college: '',
    graduationYear: '',
    major: ''
  });
  const [selectedIdentities, setSelectedIdentities] = useState([]);
  const [identityCategories, setIdentityCategories] = useState([]);
  const [identitiesByCategory, setIdentitiesByCategory] = useState({});
  const [casteSearchTerm, setCasteSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [locationDetected, setLocationDetected] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [categoryRequestSubmitted, setCategoryRequestSubmitted] = useState(false);
  const [pendingCategoryRequest, setPendingCategoryRequest] = useState(null);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const faqRef = useRef(null);
  const firstErrorRef = useRef(null);
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/dashboard';
  const referralCode = searchParams.get('ref') || localStorage.getItem('belong_ref') || '';
  const { register } = useAuth();

  // Persist referral code in localStorage so it survives page navigation during registration
  useEffect(() => {
    setCanonical('/register');
    const refParam = searchParams.get('ref');
    if (refParam) {
      localStorage.setItem('belong_ref', refParam);
    }
  }, [searchParams]);
  // Email validation function
  const validateEmail = (email) => {
    if (!email) return true; // Empty is valid (optional field)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setFormData(prev => ({ ...prev, email }));
    
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
      // Check if email is already taken (debounced)
      if (email && validateEmail(email)) {
        const timeout = setTimeout(async () => {
          try {
            const res = await axios.post('/api/auth/check-email', { email });
            if (res.data.exists) {
              setEmailError('This email is already associated with another account');
            }
          } catch (err) { /* ignore check failures */ }
        }, 500);
        // Clear previous timeout
        if (window._emailCheckTimeout) clearTimeout(window._emailCheckTimeout);
        window._emailCheckTimeout = timeout;
      }
    }
    // Clear field error
    if (fieldErrors.email) {
      setFieldErrors(prev => { const n = { ...prev }; delete n.email; return n; });
    }
  };

  // Toggle FAQ with smooth scroll
  const toggleFAQ = () => {
    setShowFAQ(prev => {
      const newValue = !prev;
      // If opening FAQ, scroll to it after a brief delay to let it render
      if (newValue) {
        setTimeout(() => {
          faqRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
      return newValue;
    });
  };

  // Auto-detect location disabled in production due to CORS issues with OpenStreetMap
  // Users can manually select their location
  // useEffect(() => {
  //   detectLocation();
  // }, []);

  const detectLocation = async () => {
    setDetectingLocation(true);
    try {
      // Try browser GPS first (accurate to street level)
      if (navigator.geolocation) {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 8000, maximumAge: 300000 });
        });
        const { latitude, longitude } = position.coords;
        const response = await axios.get(`/api/auth/detect-location?lat=${latitude}&lng=${longitude}`);
        const data = response.data;
        if (data.detected) {
          const countryCode = data.countryCode?.toUpperCase() || 'US';
          const finalCountry = countryCode === 'IN' ? 'IN' : 'US';
          const stateCode = findStateCode(finalCountry, data.state);
          setFormData(prev => ({ ...prev, currentCountry: finalCountry, currentState: stateCode }));
          setTimeout(() => {
            setFormData(prev => ({ ...prev, currentCity: data.city }));
            setLocationDetected(true);
            setDetectingLocation(false);
          }, 100);
          return;
        }
      }
    } catch (gpsErr) {
      // GPS failed, fall through to IP fallback
    }

    // Fallback: IP-based (approximate — only set country/state, let user pick city)
    try {
      const response = await axios.get('/api/auth/detect-location');
      const data = response.data;
      if (data.detected) {
        const countryCode = data.countryCode?.toUpperCase() || 'US';
        const finalCountry = countryCode === 'IN' ? 'IN' : 'US';
        const stateCode = findStateCode(finalCountry, data.state);
        // Only set country and state — IP city is often wrong, let user pick
        setFormData(prev => ({ ...prev, currentCountry: finalCountry, currentState: stateCode }));
        setLocationDetected(true);
      }
    } catch (error) {
      // Location detection failed, user can select manually
    } finally {
      setDetectingLocation(false);
    }
  };

  const loadIdentityCategories = async () => {
    try {
      // Load identity categories filtered by user's hometown + current country
      // Unless showAllCategories is on, then skip country filtering
      const hometown = formData.hometownCountry || '';
      const current = formData.currentCountry || '';
      const params = new URLSearchParams();
      if (!showAllCategories) {
        if (hometown) params.append('country', hometown);
        if (current && current !== hometown) params.append('country2', current);
      }
      const queryString = params.toString();
      
      const categoriesRes = await axios.get(`/api/identities/categories${queryString ? `?${queryString}` : ''}`);
      setIdentityCategories(categoriesRes.data.categories);

      // Load identities for each category in parallel
      const identitiesMap = {};
      const promises = categoriesRes.data.categories.map(async (category) => {
        const identitiesRes = await axios.get(
          `/api/identities/categories/${category.category_id}/identities`
        );
        identitiesMap[category.category_id] = identitiesRes.data.identities;
      });
      await Promise.all(promises);
      setIdentitiesByCategory(identitiesMap);
    } catch (err) {
      console.error('Error loading identities:', err);
      setError('Failed to load identity options');
    }
  };

  // Global search across all categories for registration
  const getRegSearchResults = () => {
    if (!searchTerm || searchTerm.length < 2) return [];
    const term = searchTerm.toLowerCase();
    const results = [];
    for (const category of identityCategories) {
      const identities = identitiesByCategory[category.category_id] || [];
      for (const identity of identities) {
        if (selectedIdentities.some(si => si.identityId === identity.identity_id)) continue;
        if (identity.name.toLowerCase().includes(term) || category.display_name.toLowerCase().includes(term)) {
          results.push({ ...identity, category });
        }
      }
    }
    return results.slice(0, 15);
  };

  const handleToggleAllCategories = async () => {
    const newValue = !showAllCategories;
    setShowAllCategories(newValue);
    // Reload categories with new filter
    try {
      const hometown = formData.hometownCountry || '';
      const current = formData.currentCountry || '';
      const params = new URLSearchParams();
      if (!newValue) {
        if (hometown) params.append('country', hometown);
        if (current && current !== hometown) params.append('country2', current);
      }
      const queryString = params.toString();
      const categoriesRes = await axios.get(`/api/identities/categories${queryString ? `?${queryString}` : ''}`);
      setIdentityCategories(categoriesRes.data.categories);
      const identitiesMap = {};
      const promises = categoriesRes.data.categories.map(async (category) => {
        const identitiesRes = await axios.get(
          `/api/identities/categories/${category.category_id}/identities`
        );
        identitiesMap[category.category_id] = identitiesRes.data.identities;
      });
      await Promise.all(promises);
      setIdentitiesByCategory(identitiesMap);
    } catch (err) {
      console.error('Error reloading categories:', err);
    }
  };

  const handleIdentityToggle = (categoryId, identityId, categoryName) => {
    setSelectedIdentities(prev => {
      // For caste category, only allow one selection (radio button behavior)
      if (categoryName === 'caste') {
        // Remove any existing caste selection
        const withoutCaste = prev.filter(i => {
          const cat = identityCategories.find(c => c.category_id === i.categoryId);
          return cat?.name !== 'caste';
        });
        // Add the new caste selection
        return [...withoutCaste, { categoryId, identityId, isPrimary: prev.length === 0 }];
      }
      
      // For other categories, allow multiple selections (checkbox behavior)
      const exists = prev.find(i => i.identityId === identityId);
      if (exists) {
        return prev.filter(i => i.identityId !== identityId);
      } else {
        // Check if already at maximum (4 identities)
        if (prev.length >= 4) {
          setError('Maximum 4 identities allowed. Remove one to add another.');
          return prev;
        }
        setError(''); // Clear any previous error
        return [...prev, { categoryId, identityId, isPrimary: prev.length === 0 }];
      }
    });
  };

  const hasOtherSelected = () => {
    return selectedIdentities.some(si => {
      const category = identityCategories.find(c => c.category_id === si.categoryId);
      return category?.is_placeholder === true;
    });
  };

  const handleSetPrimaryIdentity = (identityId) => {
    setSelectedIdentities(prev => 
      prev.map(i => ({ ...i, isPrimary: i.identityId === identityId }))
    );
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    const errors = {};
    
    // Validate first name
    if (!formData.firstName || !formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    // Validate gender
    if (!formData.gender) {
      errors.gender = 'Please select your gender';
    }
    
    // Validate email if provided
    if (formData.email && !validateEmail(formData.email)) {
      errors.email = 'Invalid email format';
      setEmailError('Invalid email format');
    }
    
    // Validate current location fields
    if (!formData.currentCountry) {
      errors.currentCountry = 'Country is required';
    }
    if (!formData.currentState) {
      errors.currentState = 'State is required';
    }
    if (!formData.currentCity) {
      errors.currentCity = 'City is required';
    }
    
    // Validate hometown location fields
    if (!formData.hometownCountry) {
      errors.hometownCountry = 'Country is required';
    }
    if (!formData.hometownState) {
      errors.hometownState = 'State is required';
    }
    if (!formData.hometownCity) {
      errors.hometownCity = 'City is required';
    }
    
    // If there are errors, highlight them and scroll to first one
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Please fill in all required fields highlighted in red');
      // Scroll to first error field
      setTimeout(() => {
        const firstErrorEl = document.querySelector('[data-field-error="true"]');
        if (firstErrorEl) {
          firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    
    // Clear any errors before proceeding
    setFieldErrors({});
    setError('');
    setEmailError('');
    
    // Move to step 4 (identity selection)
    await loadIdentityCategories();
    setStep(4);
    window.scrollTo(0, 0);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear field error when user starts typing
    if (fieldErrors[e.target.name]) {
      setFieldErrors(prev => { const next = { ...prev }; delete next[e.target.name]; return next; });
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate at least one identity selected
    if (selectedIdentities.length === 0) {
      setError('Please select at least one identity');
      return;
    }
    
    // Validate email if provided
    if (formData.email && !validateEmail(formData.email)) {
      setError('Please enter a valid email address or leave it empty');
      setEmailError('Invalid email format');
      return;
    }
    
    setError('');
    setLoading(true);

    try {
      // Register user with email
      const response = await register({
        email: formData.verifyEmail,
        firstName: formData.firstName,
        lastName: formData.lastName,
        gender: formData.gender,
        hometownCity: formData.hometownCity,
        hometownState: formData.hometownState,
        hometownCountry: formData.hometownCountry,
        currentCity: formData.currentCity,
        currentState: formData.currentState,
        currentCountry: formData.currentCountry,
        isStudent: formData.isStudent,
        college: formData.isStudent ? formData.college : null,
        graduationYear: formData.isStudent && formData.graduationYear ? parseInt(formData.graduationYear) : null,
        major: formData.isStudent ? formData.major : null
      });
      
      // Get the token from registration
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No access token received after registration');
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Add selected identities
      for (const identity of selectedIdentities) {
        await axios.post('/api/identities/me', {
          identityId: identity.identityId,
          isPrimary: identity.isPrimary
        }, { headers });
      }
      
      // Submit pending category request if exists
      if (pendingCategoryRequest) {
        try {
          await axios.post('/api/category-requests', {
            ...pendingCategoryRequest,
            selectedOtherDuringRegistration: hasOtherSelected()
          }, { headers });
        } catch (reqErr) {
          console.error('⚠️ Failed to submit category request:', reqErr);
          // Don't fail registration if category request fails
        }
      }

      // Track referral attribution if user came via a referral link
      const refCode = referralCode || localStorage.getItem('belong_ref') || '';
      if (refCode) {
        try {
          const trackRes = await axios.post('/api/referrals/track-signup', { referralCode: refCode }, { headers });
          console.log('✅ Referral tracked:', trackRes.data);
          localStorage.removeItem('belong_ref'); // Clean up
        } catch (refErr) {
          console.error('⚠️ Referral tracking failed:', refErr?.response?.data || refErr.message);
          // Don't fail registration if referral tracking fails
        }
      }
      
      setStep(5);
      trackEvent('sign_up', { method: 'email', city: formData.currentCity, country: formData.currentCountry, ref: referralCode || undefined });
      window.scrollTo(0, 0);
      setLoading(false);
    } catch (err) {
      console.error('❌ Registration error:', err);
      setError(err.response?.data?.error || err.message || 'Registration failed');
      setLoading(false);
    }
  };

  const handleCompleteProfile = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Update profile with additional fields
      const updateData = {};
      if (formData.age) updateData.age = parseInt(formData.age);
      if (formData.profession) updateData.profession = formData.profession;
      
      if (Object.keys(updateData).length > 0) {
        await axios.put('/api/users/profile', updateData, { headers });
      }
      
      // Add interests if provided
      if (formData.interests) {
        const interestsList = formData.interests.split(',').map(i => i.trim()).filter(i => i);
        if (interestsList.length > 0) {
          await axios.put('/api/users/interests', { interests: interestsList }, { headers });
        }
      }
      
      // Small delay to ensure user state is updated
      setTimeout(() => {
        navigate(returnTo);
      }, 500);
    } catch (err) {
      console.error('❌ Profile update error:', err);
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipProfile = () => {
    // Small delay to ensure user state is updated
    setTimeout(() => {
      navigate(returnTo);
    }, 500);
  };

  return (
    <div className="auth-container">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '400px' }}>
        <div className="auth-card">
          <div className="logo">
            <img src="/belong.png" alt="Belong" style={{ height: '64px', marginBottom: '8px' }} />
            <p>Find where you belong, safely</p>
          </div>

          <h2>Join Belong ✨</h2>

        {step === 1 && (
          <form onSubmit={async (e) => {
            e.preventDefault();
            setError('');
            setLoading(true);
            try {
              await axios.post('/api/auth/send-email-code', { email: formData.verifyEmail, isLogin: false });
              setStep(2);
              window.scrollTo(0, 0);
            } catch (err) {
              setError(err.response?.data?.error || 'Failed to send verification code');
            } finally {
              setLoading(false);
            }
          }}>
            <div className="form-group">
              <label>Email Address 📧</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={formData.verifyEmail}
                onChange={(e) => setFormData({ ...formData, verifyEmail: e.target.value })}
                required
              />
              <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                We'll email you a verification code 📧
              </small>
            </div>
            {error && <div className="error">{error}</div>}
            <button type="submit" className="btn btn-primary" disabled={loading || !formData.verifyEmail || !validateEmail(formData.verifyEmail)}>
              {loading ? 'Sending...' : 'Send code 📧'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={async (e) => {
            e.preventDefault();
            setError('');
            setLoading(true);
            try {
              await axios.post('/api/auth/verify-email-code', { email: formData.verifyEmail, code: formData.code });
              // Pre-fill the profile email
              setFormData(prev => ({ ...prev, email: prev.verifyEmail }));
              setStep(3);
              window.scrollTo(0, 0);
            } catch (err) {
              setError(err.response?.data?.error || 'Verification failed');
            } finally {
              setLoading(false);
            }
          }}>
            <div className="form-group">
              <label>Verification Code</label>
              <input
                type="text"
                name="code"
                placeholder="123456"
                value={formData.code}
                onChange={handleChange}
                maxLength={6}
                required
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Enter the 6-digit code we emailed you
              </small>
            </div>

            {error && <div className="error">
              {error}
              {error.includes('already registered') && (
                <div style={{ marginTop: '8px' }}>
                  <Link to="/login" style={{ color: '#007bff', fontWeight: '500' }}>Go to Login →</Link>
                </div>
              )}
            </div>}

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Checking...' : 'Let\'s go! 🚀'}
            </button>

            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => { setStep(1); window.scrollTo(0, 0); }}
              style={{ marginTop: '10px' }}
            >
              ← Back
            </button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleProfileSubmit}>
            <div className="form-group" data-field-error={fieldErrors.firstName ? 'true' : undefined}>
              <label>First Name *</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                style={fieldErrors.firstName ? { border: '2px solid #e74c3c' } : {}}
              />
              {fieldErrors.firstName && (
                <small style={{ color: '#e74c3c', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  {fieldErrors.firstName}
                </small>
              )}
            </div>

            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Email (verified) 📧 ✓</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                readOnly
                style={{ backgroundColor: '#f0f0f0' }}
              />
            </div>

            <div className="form-group" data-field-error={fieldErrors.gender ? 'true' : undefined}>
              <label>Gender *</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: fieldErrors.gender ? '2px solid #e74c3c' : '1px solid #ddd',
                  fontSize: '14px'
                }}
              >
                <option value="">Select your gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non_binary">Non-binary</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
              {fieldErrors.gender && (
                <small style={{ color: '#e74c3c', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  {fieldErrors.gender}
                </small>
              )}
            </div>

            {/* Current Location Section */}
            <div 
              data-field-error={(fieldErrors.currentCity || fieldErrors.currentState || fieldErrors.currentCountry) ? 'true' : undefined}
              style={{ 
                marginTop: '24px', 
                padding: '16px', 
                backgroundColor: locationDetected ? '#e7f3ff' : (fieldErrors.currentCity || fieldErrors.currentState || fieldErrors.currentCountry) ? '#fff5f5' : '#f5f5f5',
                borderRadius: '8px',
                border: (fieldErrors.currentCity || fieldErrors.currentState || fieldErrors.currentCountry) ? '2px solid #e74c3c' : locationDetected ? '1px solid #b3d9ff' : '1px solid #ddd'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <span style={{ fontSize: '20px' }}>📍</span>
                <strong>Where are you now?</strong>
                {detectingLocation && (
                  <span style={{ fontSize: '12px', color: '#666' }}>(Finding you...)</span>
                )}
                {locationDetected && (
                  <span style={{ fontSize: '12px', color: '#0066cc' }}>✓ Found you!</span>
                )}
              </div>
              
              {locationDetected && (
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                  We found your location! Edit it if it's not right.
                </p>
              )}

              <LocationSelector
                country={formData.currentCountry}
                state={formData.currentState}
                city={formData.currentCity}
                onCountryChange={(value) => { setFormData(prev => ({ ...prev, currentCountry: value })); setFieldErrors(prev => { const n = { ...prev }; delete n.currentCountry; return n; }); }}
                onStateChange={(value) => { setFormData(prev => ({ ...prev, currentState: value })); setFieldErrors(prev => { const n = { ...prev }; delete n.currentState; return n; }); }}
                onCityChange={(value) => { setFormData(prev => ({ ...prev, currentCity: value })); setFieldErrors(prev => { const n = { ...prev }; delete n.currentCity; return n; }); }}
                required={true}
                prefix="current"
                fieldErrors={{ country: fieldErrors.currentCountry, state: fieldErrors.currentState, city: fieldErrors.currentCity }}
              />

              {!locationDetected && !detectingLocation && (
                <button 
                  type="button"
                  onClick={detectLocation}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  📍 Detect My Location
                </button>
              )}
            </div>

            {/* Hometown Location Section */}
            <div 
              data-field-error={(fieldErrors.hometownCity || fieldErrors.hometownState || fieldErrors.hometownCountry) ? 'true' : undefined}
              style={{ 
                marginTop: '24px', 
                padding: '16px', 
                backgroundColor: (fieldErrors.hometownCity || fieldErrors.hometownState || fieldErrors.hometownCountry) ? '#fff5f5' : '#f5f5f5',
                borderRadius: '8px',
                border: (fieldErrors.hometownCity || fieldErrors.hometownState || fieldErrors.hometownCountry) ? '2px solid #e74c3c' : '1px solid #ddd'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <span style={{ fontSize: '20px' }}>🏠</span>
                <strong>Where are you from?</strong>
              </div>
              
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                Connect with people from your roots 🌱
              </p>

              <LocationSelector
                country={formData.hometownCountry}
                state={formData.hometownState}
                city={formData.hometownCity}
                onCountryChange={(value) => { setFormData(prev => ({ ...prev, hometownCountry: value })); setFieldErrors(prev => { const n = { ...prev }; delete n.hometownCountry; return n; }); }}
                onStateChange={(value) => { setFormData(prev => ({ ...prev, hometownState: value })); setFieldErrors(prev => { const n = { ...prev }; delete n.hometownState; return n; }); }}
                onCityChange={(value) => { setFormData(prev => ({ ...prev, hometownCity: value })); setFieldErrors(prev => { const n = { ...prev }; delete n.hometownCity; return n; }); }}
                required={true}
                prefix="hometown"
                fieldErrors={{ country: fieldErrors.hometownCountry, state: fieldErrors.hometownState, city: fieldErrors.hometownCity }}
              />
            </div>

            {/* Student Status Section - After location so we can filter colleges by city */}
            <div style={{ 
              marginTop: '24px', 
              padding: '16px', 
              backgroundColor: '#f0f9ff',
              borderRadius: '8px',
              border: '1px solid #bae6fd'
            }}>
              <div style={{ marginBottom: '12px' }}>
                <strong>Are you a student? 🎓</strong>
              </div>
              
              <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="isStudent"
                    value="true"
                    checked={formData.isStudent === true}
                    onChange={() => setFormData(prev => ({ ...prev, isStudent: true }))}
                    style={{ marginRight: '8px', cursor: 'pointer' }}
                  />
                  <span>Yes, I'm a student</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="isStudent"
                    value="false"
                    checked={formData.isStudent === false}
                    onChange={() => setFormData(prev => ({ 
                      ...prev, 
                      isStudent: false,
                      college: '',
                      graduationYear: '',
                      major: ''
                    }))}
                    style={{ marginRight: '8px', cursor: 'pointer' }}
                  />
                  <span>No</span>
                </label>
              </div>

              {/* Show college fields only if student */}
              {formData.isStudent && (
                <div style={{ 
                  marginTop: '16px',
                  paddingTop: '16px',
                  borderTop: '1px solid #bae6fd'
                }}>
                  <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                    Connect with people from your college! 🎓
                  </p>
                  
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label>College/University (optional)</label>
                    {formData.currentCity && getCollegesForCity(formData.currentCity).length > 1 ? (
                      <select
                        name="college"
                        value={formData.college}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Select your college</option>
                        {getCollegesForCity(formData.currentCity).map(college => (
                          <option key={college} value={college}>{college}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        name="college"
                        placeholder="e.g., UCLA, MIT, IIT Delhi"
                        value={formData.college}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '14px'
                        }}
                      />
                    )}
                    {formData.currentCity && getCollegesForCity(formData.currentCity).length > 1 && (
                      <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                        Showing colleges in {formData.currentCity}
                      </small>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Graduation Year (optional)</label>
                      <select
                        name="graduationYear"
                        value={formData.graduationYear}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Select year</option>
                        {getGraduationYearOptions().map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Major (optional)</label>
                      <input
                        type="text"
                        name="major"
                        placeholder="e.g., Computer Science"
                        value={formData.major}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <p style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '16px',
              lineHeight: '1.5'
            }}>
              Next up: Pick the identities that matter to you ✨
            </p>

            {error && <div className="error">{error}</div>}

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Hang tight... ⏳' : 'Continue 🚀'}
            </button>

            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => { setStep(2); window.scrollTo(0, 0); }}
              style={{ marginTop: '10px' }}
            >
              ← Back
            </button>
          </form>
        )}

        {step === 4 && (
          <form onSubmit={handleRegister}>
            <h3>Pick your identities ✨</h3>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '12px' }}>
              Choose the identities that matter to you. Pick at least 1, max 4.
            </p>
            
            <div style={{
              padding: '12px',
              backgroundColor: selectedIdentities.length >= 4 ? '#fff3cd' : '#e7f3ff',
              border: `1px solid ${selectedIdentities.length >= 4 ? '#ffc107' : '#b3d9ff'}`,
              borderRadius: '6px',
              marginBottom: '20px',
              fontSize: '14px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              <div>
                <strong>{selectedIdentities.length} / 4 identities selected</strong>
                {selectedIdentities.length >= 4 && (
                  <span style={{ color: '#856404', marginLeft: '8px' }}>
                    (Maximum reached)
                  </span>
                )}
              </div>
              {selectedIdentities.length > 0 && (
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  disabled={loading}
                  style={{ padding: '8px 20px', fontSize: '14px' }}
                >
                  {loading ? 'Creating...' : 'Join Belong! 🎉'}
                </button>
              )}
            </div>

            {/* Global Identity Search */}
            <div style={{ marginBottom: '12px', position: 'relative' }}>
              <input
                type="text"
                placeholder="🔍 Search any identity..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '2px solid #7c3aed',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  outline: 'none'
                }}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#999'
                  }}
                >✕</button>
              )}
              {(() => {
                const results = getRegSearchResults();
                if (searchTerm.length >= 2 && results.length > 0) {
                  return (
                    <div style={{
                      marginTop: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      {results.map(item => (
                        <div
                          key={item.identity_id}
                          onClick={() => {
                            handleIdentityToggle(item.category.category_id, item.identity_id, item.category.name);
                            setSearchTerm('');
                          }}
                          style={{
                            padding: '10px 14px',
                            borderBottom: '1px solid #f0f0f0',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <div>
                            <span style={{ fontWeight: '500' }}>{item.name}</span>
                            <span style={{ color: '#888', fontSize: '13px', marginLeft: '8px' }}>
                              {item.category.icon} {item.category.display_name}
                            </span>
                          </div>
                          <span style={{
                            backgroundColor: '#7c3aed', color: 'white', padding: '3px 10px',
                            borderRadius: '12px', fontSize: '12px', flexShrink: 0
                          }}>+ Add</span>
                        </div>
                      ))}
                    </div>
                  );
                }
                if (searchTerm.length >= 2 && results.length === 0) {
                  return (
                    <div style={{
                      marginTop: '8px', padding: '12px', backgroundColor: '#f9f9f9',
                      borderRadius: '8px', color: '#888', fontSize: '14px', textAlign: 'center'
                    }}>
                      No matching identities found. Try a different term or browse below.
                    </div>
                  );
                }
                return null;
              })()}
              <div style={{ textAlign: 'right', marginTop: '4px' }}>
                <button type="button" onClick={handleToggleAllCategories}
                  style={{ background: 'none', border: 'none', color: '#7c3aed', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}>
                  {showAllCategories ? '← Show local identities' : 'Show all countries →'}
                </button>
              </div>
            </div>

            {/* Selected identities as chips */}
            {selectedIdentities.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                {selectedIdentities.map(si => {
                  const cat = identityCategories.find(c => c.category_id === si.categoryId);
                  const ids = identitiesByCategory[si.categoryId] || [];
                  const identity = ids.find(i => i.identity_id === si.identityId);
                  return (
                    <span key={si.identityId} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '4px 10px', backgroundColor: (cat?.color || '#7c3aed') + '20',
                      border: `1px solid ${cat?.color || '#7c3aed'}`, borderRadius: '16px', fontSize: '12px'
                    }}>
                      {cat?.icon} {identity?.name || 'Unknown'}
                      <button type="button" onClick={() => handleIdentityToggle(si.categoryId, si.identityId, cat?.name)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#999', padding: 0, lineHeight: 1 }}>?</button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Category card grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '12px' }}>
              {identityCategories.map(category => {
                const catIds = identitiesByCategory[category.category_id] || [];
                const selCount = selectedIdentities.filter(si => catIds.some(ci => ci.identity_id === si.identityId)).length;
                return (
                  <div key={category.category_id}
                    onClick={() => setExpandedCategory(category.category_id)}
                    style={{
                      padding: '12px', borderRadius: '10px', cursor: 'pointer', textAlign: 'center',
                      border: `2px solid ${selCount > 0 ? category.color : '#e0e0e0'}`,
                      backgroundColor: selCount > 0 ? category.color + '10' : 'white',
                      position: 'relative'
                    }}>
                    <div style={{ fontSize: '24px', marginBottom: '4px' }}>{category.icon}</div>
                    <div style={{ fontSize: '12px', fontWeight: '600' }}>{category.display_name}</div>
                    {selCount > 0 && (
                      <span style={{
                        position: 'absolute', top: '4px', right: '4px',
                        backgroundColor: category.color, color: 'white',
                        width: '18px', height: '18px', borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '10px', fontWeight: 'bold'
                      }}>{selCount}</span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Category selection modal */}
            {expandedCategory && (() => {
              const category = identityCategories.find(c => c.category_id === expandedCategory);
              if (!category) return null;
              const catIdentities = identitiesByCategory[category.category_id] || [];
              return (
                <div style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
                  display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
                }} onClick={() => setExpandedCategory(null)}>
                  <div style={{
                    backgroundColor: 'white', borderRadius: '16px 16px 0 0',
                    width: '100%', maxWidth: '500px', maxHeight: '70vh',
                    display: 'flex', flexDirection: 'column'
                  }} onClick={e => e.stopPropagation()}>
                    <div style={{ padding: '14px 16px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '20px' }}>{category.icon}</span>
                        <strong>{category.display_name}</strong>
                      </div>
                      <button type="button" onClick={() => setExpandedCategory(null)}
                        style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666' }}>?</button>
                    </div>
                    {category.name === 'caste' && (
                      <div style={{ padding: '8px 16px 0' }}>
                        <input type="text" placeholder="?? Search..." value={casteSearchTerm}
                          onChange={(e) => setCasteSearchTerm(e.target.value)}
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }} />
                      </div>
                    )}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 16px' }}>
                      {catIdentities
                        .filter(id => !(category.name === 'caste' && casteSearchTerm) || id.name.toLowerCase().includes(casteSearchTerm.toLowerCase()))
                        .map(identity => {
                          const isSel = selectedIdentities.some(si => si.identityId === identity.identity_id);
                          const isCaste = category.name === 'caste';
                          return (
                            <div key={identity.identity_id}
                              onClick={() => handleIdentityToggle(category.category_id, identity.identity_id, category.name)}
                              style={{
                                padding: '10px 12px', marginBottom: '4px',
                                backgroundColor: isSel ? category.color + '20' : 'white',
                                border: isSel ? `2px solid ${category.color}` : '1px solid #eee',
                                borderRadius: '8px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '8px'
                              }}>
                              <input type={isCaste ? "radio" : "checkbox"} name={isCaste ? "caste" : undefined}
                                checked={isSel} onChange={() => {}} style={{ flexShrink: 0 }} />
                              <span style={{ fontSize: '14px' }}>{identity.name}</span>
                            </div>
                          );
                        })}
                    </div>
                    <div style={{ padding: '10px 16px', borderTop: '1px solid #eee' }}>
                      <button type="button" onClick={() => setExpandedCategory(null)}
                        className="btn btn-primary" style={{ width: '100%' }}>Done</button>
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Show inline prompt when "Other" is selected */}
            {hasOtherSelected() && !categoryRequestSubmitted && (
              <OtherIdentityPrompt 
                onRequestCategory={() => setShowRequestModal(true)}
                onSkip={() => {
                  // User chose to skip - just acknowledge it
                }}
              />
            )}

            {categoryRequestSubmitted && hasOtherSelected() && (
              <div style={{
                padding: '12px',
                backgroundColor: '#d4edda',
                border: '1px solid #28a745',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px',
                color: '#155724'
              }}>
                ✓ Category request submitted! We'll notify you when it's reviewed.
              </div>
            )}

            {error && <div className="error">{error}</div>}

            <button type="submit" className="btn btn-primary" disabled={loading || selectedIdentities.length === 0}>
              {loading ? 'Creating your account... ⏳' : 'Join Belong! 🎉'}
            </button>

            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => { setStep(3); window.scrollTo(0, 0); }}
              style={{ marginTop: '10px' }}
            >
              ← Back
            </button>
          </form>
        )}

        {step === 5 && (
          <form onSubmit={handleCompleteProfile}>
            <div style={{
              padding: '20px',
              backgroundColor: '#e7f3ff',
              border: '1px solid #b3d9ff',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h3 style={{ marginTop: 0 }}>🎉 You're in!</h3>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: 0 }}>
                Your account is ready! Add more details now or skip and do it later.
              </p>
            </div>

            <h3>Tell us more (optional) ✨</h3>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
              More details = better connections 🤝
            </p>

            <div className="form-group">
              <label>Age (optional)</label>
              <input
                type="number"
                name="age"
                placeholder="e.g., 25"
                value={formData.age}
                onChange={handleChange}
                min="18"
                max="120"
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                You control who sees this 🔒
              </small>
            </div>

            <div className="form-group">
              <label>What do you do? (optional) 💼</label>
              <input
                type="text"
                name="profession"
                placeholder="e.g., Software Engineer, Teacher, Student"
                value={formData.profession}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Your interests (optional) 🎨</label>
              <input
                type="text"
                name="interests"
                placeholder="e.g., Music, Sports, Technology (comma-separated)"
                value={formData.interests}
                onChange={handleChange}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Separate with commas
              </small>
            </div>

            {error && <div className="error">{error}</div>}

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving... 💾' : 'Save it ✅'}
            </button>

            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={handleSkipProfile}
              style={{ marginTop: '10px' }}
            >
              Skip for now
            </button>
          </form>
        )}

        <div className="link" style={{ marginTop: '10px' }}>
          Already have an account? <Link to="/login">Login</Link>
        </div>

        <div className="link" style={{ marginTop: '20px' }}>
          <Link to="/help" style={{ color: '#7C3AED', fontWeight: '500' }}>
            Having trouble registering? Get Help
          </Link>
        </div>

        <div className="link" style={{ marginTop: '10px' }}>
          <Link to="/privacy" style={{ color: '#7C3AED', fontWeight: '500' }}>
            🔒 Privacy Policy
          </Link>
          {' · '}
          <Link to="/terms" style={{ color: '#7C3AED', fontWeight: '500' }}>
            📜 Terms of Service
          </Link>
          {' · '}
          <Link to="/about" style={{ color: '#7C3AED', fontWeight: '500' }}>
            💜 About
          </Link>
        </div>

        <div className="link" style={{ marginTop: '20px', borderTop: '1px solid #e0e0e0', paddingTop: '20px' }}>
          <button 
            onClick={toggleFAQ}
            style={{
              background: 'none',
              border: 'none',
              color: '#007bff',
              cursor: 'pointer',
              fontSize: '15px',
              textDecoration: 'underline',
              padding: 0
            }}
          >
            {showFAQ ? '▲ Hide FAQs' : 'Have questions? View FAQs'}
          </button>
        </div>
      </div>

      <TrustBadge variant="banner" />

      <CommunityStats variant="banner" />
      {showFAQ && (
        <div ref={faqRef}>
          <FAQ />
        </div>
      )}
      
      {/* Request Category Modal */}
      <RequestCategoryModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        selectedOtherDuringRegistration={hasOtherSelected()}
        deferSubmission={true}
        onSuccess={(requestData) => {
          setShowRequestModal(false);
          setCategoryRequestSubmitted(true);
          // Store the request data to submit after registration
          if (requestData) {
            setPendingCategoryRequest(requestData);
          }
        }}
      />
      </div>
    </div>
  );
}

export default Register;